import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "fixed inset-0 z-50 flex items-center justify-center p-4" }
const _hoisted_2 = { class: "relative z-10 flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" }
const _hoisted_3 = { class: "flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4" }
const _hoisted_4 = { class: "flex items-center gap-3" }
const _hoisted_5 = {
  key: 0,
  class: "h-3 w-3",
  viewBox: "0 0 24 24",
  fill: "currentColor"
}
const _hoisted_6 = {
  key: 1,
  class: "h-3 w-3",
  fill: "none",
  viewBox: "0 0 24 24",
  stroke: "currentColor",
  "stroke-width": "2"
}
const _hoisted_7 = { class: "text-sm text-slate-500" }
const _hoisted_8 = { class: "flex min-h-0 flex-1" }
const _hoisted_9 = { class: "flex w-72 flex-shrink-0 flex-col gap-5 overflow-y-auto border-r border-slate-200 p-5" }
const _hoisted_10 = {
  key: 0,
  class: "space-y-3"
}
const _hoisted_11 = { class: "mb-1 block font-mono text-xs font-semibold text-slate-600" }
const _hoisted_12 = ["onUpdate:modelValue"]
const _hoisted_13 = {
  key: 1,
  class: "text-xs italic text-slate-400"
}
const _hoisted_14 = { class: "rounded-xl border border-amber-300 bg-amber-50 p-4" }
const _hoisted_15 = { class: "mb-3 text-[11px] text-amber-700" }
const _hoisted_16 = { class: "mb-1 block text-xs font-semibold text-slate-700" }
const _hoisted_17 = ["placeholder"]
const _hoisted_18 = {
  key: 0,
  class: "mb-2 text-[11px] text-red-600"
}
const _hoisted_19 = {
  key: 1,
  class: "mb-2"
}
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "flex-1 overflow-y-auto bg-slate-100 p-5" }
const _hoisted_22 = { class: "rounded-2xl bg-[#e5ddd5] p-5" }
const _hoisted_23 = { class: "flex justify-end" }
const _hoisted_24 = { class: "max-w-[85%] rounded-2xl rounded-tr-sm bg-[#dcf8c6] px-4 py-2.5 shadow-sm" }
const _hoisted_25 = ["innerHTML"]
const _hoisted_26 = { class: "mt-1.5 flex items-center justify-end gap-1" }
const _hoisted_27 = { class: "text-[11px] text-[#667781]" }
const _hoisted_28 = { class: "overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" }
const _hoisted_29 = { class: "space-y-1 border-b border-slate-200 bg-slate-50 px-4 py-3" }
const _hoisted_30 = { class: "text-xs text-slate-500" }
const _hoisted_31 = { class: "mt-1 text-sm font-semibold text-slate-800" }
const _hoisted_32 = ["srcdoc"]

import { ref, computed, reactive, watch } from 'vue'
import ApiService from '@/store/api.service'
import { buildEmailPreviewDocument, escapeHtml, linkify } from '@/utils/emailPreviewRenderer'
import logoBlueUrl from '@/assets/image/IOM-ITB-PrimaryLogo-blue.png'
import logoWhiteUrl from '@/assets/image/IOM-ITB-PrimaryLogo-white.png'

type MessageTemplate = {
  key: string
  title: string
  subject?: string | null
  body: string
  variables?: string[] | string
  channel: 'email' | 'whatsapp'
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PreviewPesanModal',
  props: {
    template: {},
    body: {},
    subject: {},
    sampleValues: {}
  },
  emits: ["close"],
  setup(__props: any) {

const props = __props



const testRecipient = ref('')
const isSending     = ref(false)
const testResult    = ref<{ ok: boolean; message: string } | null>(null)

const parsedVariables = computed<string[]>(() => {
  const raw = props.template.variables
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return [] }
  }
  return []
})

// inputVars: bound directly to inputs (immediate)
// localVars: debounced copy used for preview rendering
const inputVars = reactive<Record<string, string>>({})
const localVars = reactive<Record<string, string>>({})

watch(
  () => props.template.key,
  () => {
    parsedVariables.value.forEach(v => {
      inputVars[v] = props.sampleValues[v] ?? ''
      localVars[v] = props.sampleValues[v] ?? ''
    })
  },
  { immediate: true }
)

// Sync inputVars → localVars with 300ms debounce so preview only rerenders after typing pauses
let debounceTimer: ReturnType<typeof setTimeout> | null = null
watch(inputVars, (newVal) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => { Object.assign(localVars, newVal) }, 300)
}, { deep: true })

const variableTag = (name: string) => `{{${name}}}`

const substituteVars = (text: string) => {
  let result = text
  for (const [k, v] of Object.entries(localVars)) {
    result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v)
  }
  return result.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, '($1)')
}

const waPreviewHtml = computed(() => {
  const text = substituteVars(props.body)
  if (!text) return '(pesan kosong)'
  return linkify(escapeHtml(text)).replace(/\n/g, '<br />')
})

const renderedSubject = computed(() => substituteVars(props.subject))
const previewTime    = computed(() =>
  new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
)
const testSendDescription = computed(() =>
  props.template.channel === 'whatsapp'
    ? 'Pesan uji coba WhatsApp. Pengiriman ini tidak memengaruhi data produksi.'
    : 'Pesan uji coba. Email akan ditandai [TEST] dan tidak memengaruhi data produksi.'
)

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const recipientError = computed(() => {
  const r = testRecipient.value.trim()
  if (!r) return ''
  if (props.template.channel === 'email') {
    return EMAIL_RE.test(r) ? '' : 'Format email tidak valid'
  }
  // WhatsApp: accept local (08xx), country-prefixed (628xx), or E.164 (+628xx)
  const cleaned = r.replace(/[\s\-().]/g, '')
  return /^[+]?[0-9]{8,15}$/.test(cleaned) ? '' : 'Format nomor tidak valid (contoh: 628123456789 atau 08xxx)'
})

const fullEmailIframeHtml = computed(() => {
  return buildEmailPreviewDocument({
    title: props.template.title,
    bodyText: substituteVars(props.body),
    logoBlueUrl,
    logoWhiteUrl,
  })
})

const sendTest = async () => {
  if (!testRecipient.value.trim()) return
  isSending.value = true
  testResult.value = null
  try {
    await ApiService.post<any>(`email-templates/${props.template.key}/test-send`, {
      recipient: testRecipient.value.trim(),
      variables: { ...localVars },
    })
    testResult.value = { ok: true, message: 'Berhasil dikirim!' }
  } catch (err: any) {
    testResult.value = { ok: false, message: err?.response?.data?.message || 'Gagal mengirim.' }
  } finally {
    isSending.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", {
        class: "absolute inset-0 bg-black/50 backdrop-blur-sm",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
      }),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "text-lg font-bold text-slate-900" }, "Preview & Test Kirim", -1)),
            _createElementVNode("span", {
              class: _normalizeClass(["inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold", __props.template.channel === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'])
            }, [
              (__props.template.channel === 'whatsapp')
                ? (_openBlock(), _createElementBlock("svg", _hoisted_5, [...(_cache[3] || (_cache[3] = [
                    _createElementVNode("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" }, null, -1)
                  ]))]))
                : (_openBlock(), _createElementBlock("svg", _hoisted_6, [...(_cache[4] || (_cache[4] = [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                    }, null, -1)
                  ]))])),
              _createTextVNode(" " + _toDisplayString(__props.template.channel === 'whatsapp' ? 'WhatsApp' : 'Email'), 1)
            ], 2),
            _createElementVNode("span", _hoisted_7, _toDisplayString(__props.template.title), 1)
          ]),
          _createElementVNode("button", {
            type: "button",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close'))),
            class: "rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          }, [...(_cache[6] || (_cache[6] = [
            _createElementVNode("svg", {
              class: "h-5 w-5",
              fill: "none",
              viewBox: "0 0 24 24",
              stroke: "currentColor",
              "stroke-width": "2"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                d: "M6 18L18 6M6 6l12 12"
              })
            ], -1)
          ]))])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", null, [
              _cache[7] || (_cache[7] = _createElementVNode("p", { class: "mb-3 text-sm font-bold text-slate-800" }, "Isi Variabel Preview", -1)),
              (parsedVariables.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(parsedVariables.value, (v) => {
                      return (_openBlock(), _createElementBlock("div", { key: v }, [
                        _createElementVNode("label", _hoisted_11, _toDisplayString(variableTag(v)), 1),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((inputVars[v]) = $event),
                          type: "text",
                          class: "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        }, null, 8, _hoisted_12), [
                          [_vModelText, inputVars[v]]
                        ])
                      ]))
                    }), 128))
                  ]))
                : (_openBlock(), _createElementBlock("p", _hoisted_13, "Tidak ada variabel untuk template ini."))
            ]),
            _createElementVNode("div", _hoisted_14, [
              _cache[8] || (_cache[8] = _createElementVNode("div", { class: "mb-1 flex items-center gap-1.5" }, [
                _createElementVNode("svg", {
                  class: "h-4 w-4 text-amber-600",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor",
                  "stroke-width": "2"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    d: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                  })
                ]),
                _createElementVNode("p", { class: "text-xs font-bold text-amber-800" }, "Test Kirim")
              ], -1)),
              _createElementVNode("p", _hoisted_15, _toDisplayString(testSendDescription.value), 1),
              _createElementVNode("label", _hoisted_16, _toDisplayString(__props.template.channel === 'whatsapp' ? 'Nomor WhatsApp' : 'Alamat Email'), 1),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((testRecipient).value = $event)),
                type: "text",
                placeholder: __props.template.channel === 'whatsapp' ? '628123456789' : 'test@example.com',
                class: _normalizeClass(["mb-1 w-full rounded-lg border px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2", recipientError.value && testRecipient.value.trim()
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-500/20'
                  : 'border-slate-200 focus:border-blue-400 focus:ring-blue-500/20'])
              }, null, 10, _hoisted_17), [
                [_vModelText, testRecipient.value]
              ]),
              (recipientError.value && testRecipient.value.trim())
                ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(recipientError.value), 1))
                : (_openBlock(), _createElementBlock("div", _hoisted_19)),
              _createElementVNode("button", {
                type: "button",
                onClick: sendTest,
                disabled: isSending.value || !testRecipient.value.trim() || !!recipientError.value,
                class: "w-full rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white transition-all hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
              }, _toDisplayString(isSending.value ? 'Mengirim...' : (__props.template.channel === 'whatsapp' ? 'Kirim ke WhatsApp' : 'Kirim ke Email')), 9, _hoisted_20),
              (testResult.value)
                ? (_openBlock(), _createElementBlock("p", {
                    key: 2,
                    class: _normalizeClass(["mt-2 text-xs font-medium", testResult.value.ok ? 'text-green-700' : 'text-red-600'])
                  }, _toDisplayString(testResult.value.message), 3))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_21, [
            (__props.template.channel === 'whatsapp')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _cache[10] || (_cache[10] = _createElementVNode("p", { class: "mb-3 text-center text-xs text-slate-500" }, "Tampilan perkiraan pesan di WhatsApp", -1)),
                  _createElementVNode("div", _hoisted_22, [
                    _createElementVNode("div", _hoisted_23, [
                      _createElementVNode("div", _hoisted_24, [
                        _createElementVNode("div", {
                          class: "break-words text-[14.5px] leading-relaxed text-[#111b21]",
                          innerHTML: waPreviewHtml.value || '(pesan kosong)'
                        }, null, 8, _hoisted_25),
                        _createElementVNode("div", _hoisted_26, [
                          _createElementVNode("span", _hoisted_27, _toDisplayString(previewTime.value), 1),
                          _cache[9] || (_cache[9] = _createElementVNode("svg", {
                            class: "h-[13px] w-[18px] flex-shrink-0",
                            viewBox: "0 0 20 13",
                            fill: "none",
                            stroke: "#53bdeb",
                            "stroke-width": "2.2",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          }, [
                            _createElementVNode("path", { d: "M1 7L5.5 11.5L13.5 2" }),
                            _createElementVNode("path", { d: "M7 7L11.5 11.5L19.5 2" })
                          ], -1))
                        ])
                      ])
                    ])
                  ])
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _cache[13] || (_cache[13] = _createElementVNode("p", { class: "mb-3 text-center text-xs text-slate-500" }, "Tampilan perkiraan email yang diterima pemohon", -1)),
                  _createElementVNode("div", _hoisted_28, [
                    _createElementVNode("div", _hoisted_29, [
                      _cache[12] || (_cache[12] = _createElementVNode("p", { class: "text-xs text-slate-500" }, [
                        _createElementVNode("span", { class: "font-semibold text-slate-700" }, "Dari:"),
                        _createTextVNode(" IOM ITB <noreply@iom-itb.ac.id> ")
                      ], -1)),
                      _createElementVNode("p", _hoisted_30, [
                        _cache[11] || (_cache[11] = _createElementVNode("span", { class: "font-semibold text-slate-700" }, "Kepada:", -1)),
                        _createTextVNode(" " + _toDisplayString(testRecipient.value || 'pemohon@example.com'), 1)
                      ]),
                      _createElementVNode("p", _hoisted_31, _toDisplayString(renderedSubject.value || '(tanpa subject)'), 1)
                    ]),
                    _createElementVNode("iframe", {
                      srcdoc: fullEmailIframeHtml.value,
                      class: "w-full border-0",
                      style: {"height":"650px"},
                      sandbox: "allow-same-origin"
                    }, null, 8, _hoisted_32)
                  ])
                ], 64))
          ])
        ])
      ])
    ])
  ]))
}
}

})